import { Box, Stack, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Layers } from "@material-ui/icons";
import Label from "components/Label";
import { getListingAddressString } from "utils/Haim/utils";
import type { ProcessedListingAddress } from "../../../@types/sharedSchema";

/**
 * One picked GROUP, standing in for the N locations it expanded into.
 *
 * ── Why it has to look like a different species ───────────────────────────────
 *
 * It sits in the same row as plain location chips, which are `Label` in
 * `variant="ghost"` — `color="primary"` inside the picker (`LocationPicker.tsx`)
 * and `color="secondary"` in the committed filter rows
 * (`SearchStateFilterChips.tsx`). Those are the app's green and blue. If a group
 * chip were merely a fourth pastel pill, deleting it would look like deleting one
 * area and would silently remove up to fifty — so the difference is carried by
 * three signals at once rather than by colour alone: an amber palette nothing
 * else in a chip row uses, an outline no ghost chip has, and a leading
 * icon/emoji plus a trailing count that plain chips structurally cannot show.
 *
 * `Label` stays the primitive on purpose: it fixes the height at 22px, so the
 * group chip still sits on the same baseline as its neighbours.
 *
 * ── Two mechanics that are easy to get wrong ──────────────────────────────────
 *
 * - `variant="filled"` is unusable here. `Label` renders its delete affordance as
 *   `<Close color={color} />`, i.e. `palette[color].main` — which on a FILLED
 *   chip is painted onto `palette[color].main` and disappears. Ghost + an sx
 *   outline gets the emphasis without the invisible ✕.
 * - `Label` is a plain function component and forwards no ref, and it ignores
 *   `onClick` (it only spreads `onMouseDown`). So the click target and the
 *   `Tooltip` anchor are both the `Box` wrapper, never the `Label` itself.
 *
 * ── The two overrides must follow the MODE, not a fixed end of the ramp ───────
 *
 * `Label`'s ghost variant paints its own text `palette[color][isLight ? "dark" :
 * "light"]` over a 16% wash of `main`, because the surface underneath flips: white
 * in light mode, `GREY[800]` in dark. These two overrides sit on that same wash and
 * so owe the same rule. Pinned to `warning.darker` / `warning.dark`, the count
 * badge measured about 1.5:1 in dark mode and the ✕ about 2.8:1 — an amber chip
 * whose number is a smudge, on the one chip in the row that removes up to fifty
 * areas at a click.
 */

/** Enough of the members to recognise the group; the rest is a "+N". */
const TOOLTIP_MEMBERS_SHOWN = 8;

type LocationGroupChipProps = {
  name: string;
  count: number;
  emoji?: string;
  /** The members, for the tooltip. Absent = no tooltip (nothing truthful to list). */
  locations?: ProcessedListingAddress[];
  onDelete?: () => void;
  onClick?: () => void;
  disabled?: boolean;
};

export function LocationGroupChip({
  name,
  count,
  emoji,
  locations,
  onDelete,
  onClick,
  disabled
}: LocationGroupChipProps): JSX.Element {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  /** The ink for anything drawn on the chip's own 16% warning wash. See the header. */
  const inkStrong = isLight ? theme.palette.warning.darker : theme.palette.warning.lighter;
  const inkSoft = isLight ? theme.palette.warning.dark : theme.palette.warning.light;

  const clickable = Boolean(onClick) && !disabled;

  const members = locations ?? [];
  const shown = members.slice(0, TOOLTIP_MEMBERS_SHOWN);
  const hidden = members.length - shown.length;

  const chip = (
    <Label
      variant="ghost"
      color="warning"
      disabled={disabled}
      // `Label`'s own signature types the handler against the ✕ SvgIcon; the
      // caller only ever wants "the chip was dismissed".
      // The ✕ lives INSIDE the clickable anchor below, so its click has to be
      // stopped here or removing a group would also open that group's editor —
      // a dialog for something that no longer exists.
      onDelete={
        onDelete
          ? (event?: { stopPropagation?: () => void }) => {
              event?.stopPropagation?.();
              onDelete();
            }
          : undefined
      }
      sx={{
        gap: 0.5,
        maxWidth: 260,
        border: "1px solid",
        borderColor: alpha(theme.palette.warning.main, 0.48),
        // `Label`'s RootStyle hardcodes `cursor: default`, and it is the element
        // actually under the pointer — a `cursor` on the wrapper below never wins.
        // The chip opens its group, so it has to say so.
        ...(clickable && { cursor: "pointer" }),
        // Both the leading badge and `Label`'s ✕ — the ✕ arrives painted
        // `warning.main`, which is too pale against a 16% wash of itself.
        // The ✕ keeps its own cursor: it does something different from the chip.
        "& svg": { color: inkSoft },
        ...(onDelete && { "& > svg:first-of-type": { cursor: "pointer" } })
      }}
    >
      {emoji ? (
        <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>
          {emoji}
        </Box>
      ) : (
        <Layers sx={{ width: 13, height: 13 }} />
      )}
      <Box
        component="span"
        sx={{
          lineHeight: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {name}
      </Box>
      <Box
        component="span"
        sx={{
          lineHeight: 1,
          px: 0.5,
          py: "2px",
          borderRadius: "6px",
          fontSize: 11,
          fontWeight: 700,
          color: inkStrong,
          backgroundColor: alpha(theme.palette.warning.main, 0.32)
        }}
      >
        {count}
      </Box>
    </Label>
  );

  const anchor = (
    <Box
      component="span"
      onClick={clickable ? onClick : undefined}
      sx={{
        // `alignItems` rather than `verticalAlign`: this chip shares a row with
        // plain `Label`s, and the two used to sit a couple of pixels apart because
        // one aligned on its box and the other on the text baseline. Every row that
        // renders it is now a flex container, where `verticalAlign` is inert anyway.
        display: "inline-flex",
        alignItems: "center",
        cursor: clickable ? "pointer" : "default"
      }}
    >
      {chip}
    </Box>
  );

  if (!members.length) return anchor;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Stack spacing={0.25} sx={{ py: 0.5 }}>
          {shown.map((location, index) => (
            <Typography key={index} variant="caption" sx={{ display: "block" }}>
              {getListingAddressString(location)}
            </Typography>
          ))}
          {hidden > 0 && (
            <Typography variant="caption" sx={{ display: "block", opacity: 0.72 }}>
              {`+${hidden}`}
            </Typography>
          )}
        </Stack>
      }
    >
      {anchor}
    </Tooltip>
  );
}
